import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";

import "../component/quotation/quotationPage.css";

import QuotationApi from "../utils/quotationApi";
import QuotationHeader from "../component/quotation/quotationHeader";
import QuotationStatus from "../component/quotation/quotationStatus";
import QuotationCustomer from "../component/quotation/quotationCustomer";
import QuotationItems from "../component/quotation/quotationItems";
import QuotationTerms from "../component/quotation/quotationTerms";
import QuotationFooter from "../component/quotation/quotationFooter";
import QuotationActions from "../component/quotation/quotationActions";

const quotationApi = QuotationApi();

const QuotationPage = () => {
  const { token } = useParams();

  const [quotation, setQuotation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [accepting, setAccepting] = useState(false);
  const [submittingResponse, setSubmittingResponse] = useState(false);

  const [showAcceptDialog, setShowAcceptDialog] = useState(false);

  // Fetch public quotation
  useEffect(() => {
    const fetchQuotation = async () => {
      try {
        setLoading(true);
        setError("");

        const result = await quotationApi.getPublicQuotation(token);

        if (!result?.success) {
          throw new Error(result?.message || "Unable to load quotation.");
        }

        const resultQuotation = result?.data?.quotation || null;

        setQuotation(resultQuotation);
      } catch (err) {
        console.error("Failed to load quotation:", err);

        setError(
          err?.response?.data?.message ||
            err?.message ||
            "Unable to load quotation.",
        );
      } finally {
        setLoading(false);
      }
    };

    if (token) {
      fetchQuotation();
    }
  }, [token]);

  // Open acceptance confirmation dialog
  const handleAcceptClick = () => {
    if (accepting || !quotation || quotation.status !== "SENT") {
      return;
    }

    setShowAcceptDialog(true);
  };

  // Confirm and accept quotation
  const handleConfirmAccept = async () => {
    if (accepting || !quotation) return;

    try {
      setAccepting(true);

      const result = await quotationApi.decidePublicQuotation(token, {
        decision: "ACCEPTED",
      });

      if (!result?.success) {
        throw new Error(result?.message || "Unable to accept quotation.");
      }

      setQuotation((previous) => ({
        ...previous,
        status: "ACCEPTED",
      }));

      setShowAcceptDialog(false);

      return result;
    } catch (err) {
      const message =
        err?.response?.data?.message ||
        err?.message ||
        "Unable to accept quotation. Please try again.";

      throw new Error(message);
    } finally {
      setAccepting(false);
    }
  };

  // Submit discussion request or decline response
  const handleQuotationResponse = async (responseData) => {
    if (submittingResponse || !quotation || quotation.status !== "SENT") {
      throw new Error("This quotation can no longer receive a response.");
    }

    try {
      setSubmittingResponse(true);

      const result = await quotationApi.decidePublicQuotation(
        token,
        responseData,
      );
    
      if (!result?.success) {
        throw new Error(result?.message || "Unable to submit your response.");
      }

      // Both DISCUSSION_REQUEST and DECLINED change quotation status.
      if (
        responseData.decision === "DECLINED" ||
        responseData.decision === "DISCUSSION_REQUEST"
      ) {
        setQuotation((previous) => ({
          ...previous,
          status: result.data.quotation.status,
        }));
      }

      return result;
    } catch (err) {
      const message =
        err?.response?.data?.message ||
        err?.message ||
        "Unable to submit your response. Please try again.";

      throw new Error(message);
    } finally {
      setSubmittingResponse(false);
    }
  };

  if (loading) {
    return (
      <div className="quotation-page">
        <div className="quotation-loading">Loading quotation...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="quotation-page">
        <div className="quotation-error">
          <h2>Unable to Load Quotation</h2>
          <p>{error}</p>
        </div>
      </div>
    );
  }

  if (!quotation) {
    return (
      <div className="quotation-page">
        <div className="quotation-loading">Quotation not found.</div>
      </div>
    );
  }

  const isAccepted = quotation.status === "ACCEPTED";
  const isRejected = quotation.status === "REJECTED";
  const isFinalDecision = isAccepted || isRejected;

  return (
    <div className="quotation-page">
      <QuotationHeader quotation={quotation} />

      <main className="quotation-container">
        <QuotationStatus quotation={quotation} />

        <QuotationCustomer quotation={quotation} />

        <QuotationItems quotation={quotation} />

        <QuotationActions
          quotation={quotation}
          onAccept={handleAcceptClick}
          accepting={accepting}
          onSubmitResponse={handleQuotationResponse}
          submittingResponse={submittingResponse}
          disabled={isFinalDecision}
        />

        {/* Final decision message */}
        {isFinalDecision && (
          <div
            className={`quotation-decision-message ${
              isAccepted ? "accepted" : "rejected"
            }`}
          >
            <div className="decision-message-icon">
              {isAccepted ? "✓" : "ℹ"}
            </div>

            <div className="decision-message-content">
              <h3>Decision Already Submitted</h3>

              {isAccepted ? (
                <p>
                  You have accepted this quotation. Thank you for your
                  confirmation. Our company team will contact you shortly to
                  arrange a suitable work schedule based on the work mentioned
                  in your quotation.
                </p>
              ) : (
                <p>
                  You have declined this quotation. Thank you for letting us
                  know.
                </p>
              )}
            </div>
          </div>
        )}

        <QuotationTerms quotation={quotation} />
      </main>

      <QuotationFooter quotation={quotation} />

      {/* Acceptance confirmation dialog */}
      {showAcceptDialog && (
        <div className="quotation-dialog-overlay">
          <div
            className="quotation-confirm-dialog"
            role="dialog"
            aria-modal="true"
            aria-labelledby="accept-dialog-title"
          >
            <div className="quotation-dialog-icon">✓</div>

            <h2 id="accept-dialog-title">Accept Quotation?</h2>

            <p>Are you sure you want to accept this quotation?</p>

            <p>
              Thank you for choosing us. Once accepted, our company team will
              contact you shortly to confirm a suitable work schedule based on
              the work mentioned in your quotation.
            </p>

            <div className="quotation-dialog-actions">
              <button
                type="button"
                className="quotation-dialog-cancel"
                onClick={() => setShowAcceptDialog(false)}
                disabled={accepting}
              >
                Cancel
              </button>

              <button
                type="button"
                className="quotation-dialog-confirm"
                onClick={handleConfirmAccept}
                disabled={accepting}
              >
                {accepting ? "Accepting..." : "Confirm Acceptance"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default QuotationPage;
